import { hash32, mulberry32 } from "../util/rng.js";

/**
 * Slot clock. Pure arithmetic, all in UTC.
 *
 * The station airs fixed 60-second slots keyed by `floor(now / 60_000)`. Every
 * listener in the same slot hears the same CVE, chosen deterministically from
 * the slot index. `resolvePosition` is the single public entry point so that
 * "recompute before you play" is enforced in one place rather than remembered
 * at each call site.
 */

export const SLOT_MS = 60_000;

export interface SlotPosition {
  /** Absolute slot number since the Unix epoch. */
  slotIndex: number;
  /** Seconds elapsed inside the slot, in [0, 60). */
  offsetSec: number;
  /** Epoch milliseconds of the slot boundary. */
  slotStartMs: number;
}

export function slotStartMs(slotIndex: number, slotMs: number = SLOT_MS): number {
  return slotIndex * slotMs;
}

export function resolvePosition(nowMs: number, slotMs: number = SLOT_MS): SlotPosition {
  const slotIndex = Math.floor(nowMs / slotMs);
  const slotStart = slotIndex * slotMs;
  return {
    slotIndex,
    offsetSec: (nowMs - slotStart) / 1000,
    slotStartMs: slotStart,
  };
}

/** Epoch ms of the next boundary strictly after `nowMs`. */
export function nextBoundaryMs(nowMs: number, slotMs: number = SLOT_MS): number {
  const position = resolvePosition(nowMs, slotMs);
  return slotStartMs(position.slotIndex + 1, slotMs);
}

export function startOfUtcDay(nowMs: number): number {
  const date = new Date(nowMs);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export function isoUtcDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * The pool window: [UTC midnight of the current day - 7 days, UTC midnight of
 * the current day). Day bucketing is what makes cross-listener pool agreement
 * possible at all; a rolling 7x24h window would drift every minute.
 */
export interface PoolWindow {
  startMs: number;
  endMs: number;
  startUtc: string;
  endUtc: string;
}

export function poolWindowFor(nowMs: number, days = 7): PoolWindow {
  const endMs = startOfUtcDay(nowMs);
  // Day arithmetic through Date.UTC so DST never enters the picture.
  const start = new Date(endMs);
  const startMs = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() - days);
  return {
    startMs,
    endMs,
    startUtc: isoUtcDate(startMs),
    endUtc: isoUtcDate(endMs),
  };
}

/**
 * Deterministic pick for a slot. Same slot index + same pool size => same index,
 * on every client, forever.
 */
export function pickIndex(slotIndex: number, poolSize: number): number {
  if (poolSize <= 0) return -1;
  const next = mulberry32(hash32(`slot:${slotIndex}`));
  const index = Math.floor(next() * poolSize);
  return index % poolSize;
}
